import { FC, useState, useEffect } from 'react';
import { WhatsAppStatus } from '../../api/whatsapp';
import { useToast } from '../ui/Toast';
import { useAuth } from '../../context/AuthContext';
import { testAffiliateConnection } from '../../api/offers';
import {
  AffiliateSettings,
  getAffiliateSettings,
  saveAffiliateSettings,
} from '../../utils/affiliateSettings';
import { fetchTenantSettings, updateTenantSettings } from '../../api/settings';
import { AffiliatesSettingsTab } from './tabs/AffiliatesSettingsTab';
import { BrandingSettingsTab } from './tabs/BrandingSettingsTab';
import { TrackingSettingsTab } from './tabs/TrackingSettingsTab';
import { IntegrationsSettingsTab } from './tabs/IntegrationsSettingsTab';
import { ProfileSettingsTab } from './tabs/ProfileSettingsTab';
import { PlansSettingsTab } from './tabs/PlansSettingsTab';
import { CsvReconciliationTab } from './tabs/CsvReconciliationTab';

export type SettingsTabId =
  | 'affiliates'
  | 'branding'
  | 'tracking'
  | 'integrations'
  | 'profile'
  | 'plans'
  | 'conciliacao-csv';

interface SettingsTabOption {
  id: SettingsTabId;
  label: string;
}

const SETTINGS_TABS: SettingsTabOption[] = [
  { id: 'affiliates', label: 'Afiliados' },
  { id: 'conciliacao-csv', label: 'Conciliação CSV' },
  { id: 'branding', label: 'Identidade e Vitrine' },
  { id: 'tracking', label: 'Pixels e Rastreamento' },
  { id: 'integrations', label: 'Conexoes e Sistema' },
  { id: 'profile', label: 'Perfil' },
  { id: 'plans', label: 'Planos e Pagamentos' },
];

interface SettingsViewProps {
  whatsappStatus: WhatsAppStatus | null;
  onRefreshWhatsApp: () => void;
  onDisconnectWhatsApp: () => void;
  onNavigateToWhatsApp?: () => void;
  initialTab?: SettingsTabId;
}

export const SettingsView: FC<SettingsViewProps> = ({
  whatsappStatus,
  onRefreshWhatsApp,
  onDisconnectWhatsApp,
  onNavigateToWhatsApp,
  initialTab,
}) => {
  const { token } = useAuth();
  const { success, error: toastError } = useToast();

  const [activeTab, setActiveTab] = useState<SettingsTabId>(() => {
    if (initialTab) return initialTab;
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.replace('#', '');
      const path = window.location.pathname;
      if (
        hash === 'conciliacao-csv' ||
        path.includes('/settings/conciliacao-csv') ||
        path.includes('/conciliacao-csv')
      ) {
        return 'conciliacao-csv';
      }
      if (
        hash === 'affiliates' ||
        hash === 'branding' ||
        hash === 'tracking' ||
        hash === 'integrations' ||
        hash === 'profile' ||
        hash === 'plans' ||
        hash === 'conciliacao-csv'
      ) {
        return hash as SettingsTabId;
      }
    }
    return 'affiliates';
  });

  const [affiliateSettings, setAffiliateSettings] = useState<AffiliateSettings>(
    () => getAffiliateSettings()
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [testingMarketplace, setTestingMarketplace] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (!token) return;
    fetchTenantSettings(token)
      .then((data) => {
        setAffiliateSettings(data);
        saveAffiliateSettings(data);
      })
      .catch(() => {
        setAffiliateSettings(getAffiliateSettings());
      });
  }, [token]);

  const handleTabChange = (tabId: SettingsTabId) => {
    setActiveTab(tabId);
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', `#${tabId}`);
    }
  };

  const handleSaveAffiliate = async () => {
    setIsSaving(true);
    try {
      if (token) {
        const updated = await updateTenantSettings(token, affiliateSettings);
        setAffiliateSettings(updated);
        saveAffiliateSettings(updated);
      } else {
        saveAffiliateSettings(affiliateSettings);
      }
      setIsSaved(true);
      success('Configurações salvas com sucesso!', 'Salvo');
      setTimeout(() => setIsSaved(false), 2500);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toastError(msg, 'Erro ao Salvar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async (marketplace: string) => {
    if (!token) {
      toastError(
        'Você precisa estar logado para testar conexões de API.',
        'Autenticação'
      );
      return;
    }

    setTestingMarketplace(marketplace);
    try {
      let payload: Parameters<typeof testAffiliateConnection>[1];
      if (marketplace === 'shopee') {
        payload = {
          marketplace: 'shopee',
          app_id: affiliateSettings.shopeeAppId,
          secret_key: affiliateSettings.shopeeSecret,
        };
      } else if (marketplace === 'mercadolivre') {
        payload = {
          marketplace: 'mercadolivre',
          app_id: affiliateSettings.mercadolivreAppId,
          secret_key: affiliateSettings.mercadolivreSecretKey,
          access_token: affiliateSettings.mercadolivreAccessToken,
          partner_tag: affiliateSettings.mercadolivreTag,
        };
      } else {
        payload = { marketplace };
      }

      const res = await testAffiliateConnection(token, payload);
      if (res.success) {
        success(res.message, 'Conexão Confirmada');
      } else {
        toastError(res.message, 'Falha no Teste');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toastError(msg, 'Erro de Conexão');
    } finally {
      setTestingMarketplace(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h2 className="text-base font-semibold text-content-main">
          Configurações
        </h2>
        <p className="text-xs text-content-muted">
          Gerenciamento de credenciais de afiliados, vitrine, rastreamento e
          integrações
        </p>
      </div>

      {/* Submenu de Navegação (Tabs Segmentadas) */}
      <div className="border-b border-surface-border">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {SETTINGS_TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={`cursor-pointer rounded-lg px-3.5 py-2 text-xs font-medium transition-colors ${
                  isActive
                    ? 'border border-surface-border bg-surface-subtle font-semibold text-content-main'
                    : 'hover:bg-surface-subtle/50 text-content-muted hover:text-content-main'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Renderização Condicional da Aba Selecionada */}
      {activeTab === 'affiliates' && (
        <AffiliatesSettingsTab
          affiliateSettings={affiliateSettings}
          setAffiliateSettings={setAffiliateSettings}
          isSaving={isSaving}
          isSaved={isSaved}
          testingMarketplace={testingMarketplace}
          onSave={handleSaveAffiliate}
          onTestConnection={handleTestConnection}
        />
      )}

      {activeTab === 'conciliacao-csv' && <CsvReconciliationTab />}

      {activeTab === 'branding' && (
        <BrandingSettingsTab
          affiliateSettings={affiliateSettings}
          setAffiliateSettings={setAffiliateSettings}
          isSaving={isSaving}
          onSave={handleSaveAffiliate}
        />
      )}

      {activeTab === 'tracking' && (
        <TrackingSettingsTab
          affiliateSettings={affiliateSettings}
          setAffiliateSettings={setAffiliateSettings}
          isSaving={isSaving}
          onSave={handleSaveAffiliate}
        />
      )}

      {activeTab === 'integrations' && (
        <IntegrationsSettingsTab
          whatsappStatus={whatsappStatus}
          onRefreshWhatsApp={onRefreshWhatsApp}
          onDisconnectWhatsApp={onDisconnectWhatsApp}
          onNavigateToWhatsApp={onNavigateToWhatsApp}
        />
      )}

      {activeTab === 'profile' && <ProfileSettingsTab />}

      {activeTab === 'plans' && <PlansSettingsTab />}
    </div>
  );
};
